import { SaleCreateData } from "../stores/useSaleCreateStore"
import { Sale, Bid, LumpSumBid, PayAsCutBid } from "../types"
import {
  ApiSaleRequest,
  ApiSaleResponse,
  ApiError as ApiErrorType,
} from "../types/api"
import { ApiError, formatApiError } from "./apiUtils"
import { postProtected, getProtected, patchProtected } from "../../../api/auth"

class SalesApiError extends ApiError {
  constructor(message: string, status?: number, apiError?: ApiErrorType) {
    super(message, status, apiError)
    this.name = "SalesApiError"
  }
}

// Helper function to convert date strings or Date objects to ISO strings
const toISOStringSafe = (date: Date | string | undefined): string => {
  if (!date) return ""
  const dateObj = typeof date === "string" ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ""
  return dateObj.toISOString()
}

// Transform store data to API format
const transformSaleDataToApiFormat = (
  saleData: SaleCreateData,
  status: "draft" | "active" | "closed" = "draft"
): ApiSaleRequest => {
  // Convert boundary geometry to MultiPolygon if needed
  let boundaryGeom: GeoJSON.MultiPolygon | undefined
  if (saleData.boundary_geom) {
    if (saleData.boundary_geom.type === "Polygon") {
      // Convert Polygon to MultiPolygon
      boundaryGeom = {
        type: "MultiPolygon",
        coordinates: [saleData.boundary_geom.coordinates],
      }
    } else {
      // Already MultiPolygon
      boundaryGeom = saleData.boundary_geom
    }
  }

  return {
    name: saleData.name,
    location_text: saleData.location_text,
    description: saleData.description || undefined,
    boundary_geom: boundaryGeom,
    sale_type: saleData.sale_type,
    status: status,
    bid_start_datetime: toISOStringSafe(saleData.bid_start_datetime),
    bid_stop_datetime: toISOStringSafe(saleData.bid_stop_datetime),
    bid_select_datetime: toISOStringSafe(saleData.bid_select_datetime),
    bidding_tc: saleData.bidding_tc || undefined,
    contract_tc: saleData.contract_tc || undefined,
    inventory_description: saleData.inventory_description || undefined,
    timber_products: saleData.timber_products || [],
  }
}

// Validate required fields before API call
const validateSaleData = (saleData: SaleCreateData): string[] => {
  const errors: string[] = []

  if (!saleData.name?.trim()) {
    errors.push("Sale name is required")
  }

  if (!saleData.location_text?.trim()) {
    errors.push("Location is required")
  }

  if (!saleData.sale_type) {
    errors.push("Sale type is required")
  }

  if (!saleData.bid_start_datetime) {
    errors.push("Bid start date/time is required")
  }

  if (!saleData.bid_stop_datetime) {
    errors.push("Bid stop date/time is required")
  }

  if (!saleData.bid_select_datetime) {
    errors.push("Bid selection date/time is required")
  }

  // Validate date logic
  if (saleData.bid_start_datetime && saleData.bid_stop_datetime) {
    if (saleData.bid_start_datetime >= saleData.bid_stop_datetime) {
      errors.push("Bid start time must be before bid stop time")
    }
  }

  if (saleData.bid_stop_datetime && saleData.bid_select_datetime) {
    if (saleData.bid_stop_datetime >= saleData.bid_select_datetime) {
      errors.push("Bid stop time must be before bid selection time")
    }
  }

  return errors
}

// Main API function to create a sale
export const createSale = async (
  saleData: SaleCreateData,
  shouldPublish: boolean = false
): Promise<ApiSaleResponse> => {
  const status = shouldPublish ? "active" : "draft"

  // Client-side validation
  const validationErrors = validateSaleData(saleData)
  if (validationErrors.length > 0) {
    throw new SalesApiError(
      `Validation failed:\n${validationErrors.join("\n")}`,
      400
    )
  }

  // Additional validation if publishing
  if (shouldPublish) {
    const publishValidationErrors = validateSaleForPublishing({
      ...saleData,
      status: "draft", // Temp status for validation function
      id: 0, // Temp ID for validation function
      created_at: new Date(),
      bid_count: 0,
    } as Sale)

    if (publishValidationErrors.length > 0) {
      throw new SalesApiError(
        `Sale cannot be published due to the following issues:\n${publishValidationErrors.join(
          "\n"
        )}`,
        400
      )
    }
  }

  try {
    // Transform data to API format
    const apiPayload = transformSaleDataToApiFormat(saleData, status)

    // Make API request using protected endpoint
    const response = await postProtected(
      "birddog/sales/",
      apiPayload,
      undefined,
      "birddog"
    )
    const createdSale: ApiSaleResponse = response.data
    return createdSale
  } catch (error: any) {
    // Re-throw our custom errors
    if (error instanceof SalesApiError) {
      throw error
    }

    // Handle API errors from protected endpoints
    if (error?.response?.data) {
      const formattedError = formatApiError(error.response.data as ApiErrorType)
      throw new SalesApiError(
        formattedError,
        error.response.status as number,
        error.response.data as ApiErrorType
      )
    }

    // Handle network errors or other unexpected errors
    if (error instanceof Error) {
      throw new SalesApiError(`Network error: ${error.message}`, 0)
    }

    throw new SalesApiError("An unexpected error occurred")
  }
}

// Transform API sales response to frontend format
const transformApiSalesToFrontend = (apiSales: ApiSaleResponse[]): Sale[] => {
  return apiSales.map((sale) => ({
    ...sale, // Most fields match exactly now that IDs are numbers
    // Handle boundary_geom type - API returns MultiPolygon | undefined, frontend expects Polygon | MultiPolygon
    boundary_geom: sale.boundary_geom || {
      type: "Polygon" as const,
      coordinates: [[]],
    },
    created_at: new Date(sale.created_at),
    updated_at: sale.updated_at ? new Date(sale.updated_at) : undefined,
    published_at: sale.published_at ? new Date(sale.published_at) : undefined,
    bid_start_datetime: new Date(sale.bid_start_datetime),
    bid_stop_datetime: new Date(sale.bid_stop_datetime),
    bid_select_datetime: new Date(sale.bid_select_datetime),
  }))
}

// Fetch all sales from API and transform to frontend format
export const fetchSales = async (): Promise<Sale[]> => {
  try {
    const response = await getProtected("birddog/sales/", undefined, "birddog")
    const apiSales: ApiSaleResponse[] = response.data
    return transformApiSalesToFrontend(apiSales)
  } catch (error: any) {
    // Handle API errors from protected endpoints
    if (error?.response?.data) {
      const formattedError = formatApiError(error.response.data as ApiErrorType)
      throw new SalesApiError(
        formattedError,
        error.response.status as number,
        error.response.data as ApiErrorType
      )
    }

    throw new SalesApiError(
      `Failed to fetch sales: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    )
  }
}

// Transform single API sale response to frontend format
const transformApiSaleToFrontend = (apiSale: ApiSaleResponse): Sale => {
  return {
    ...apiSale, // Most fields match exactly now that IDs are numbers
    // Handle boundary_geom type - API returns MultiPolygon | undefined, frontend expects Polygon | MultiPolygon
    boundary_geom: apiSale.boundary_geom || {
      type: "Polygon" as const,
      coordinates: [[]],
    },
    created_at: new Date(apiSale.created_at),
    updated_at: apiSale.updated_at ? new Date(apiSale.updated_at) : undefined,
    published_at: apiSale.published_at
      ? new Date(apiSale.published_at)
      : undefined,
    bid_start_datetime: new Date(apiSale.bid_start_datetime),
    bid_stop_datetime: new Date(apiSale.bid_stop_datetime),
    bid_select_datetime: new Date(apiSale.bid_select_datetime),
  }
}

// Fetch individual sale from API by ID
export const fetchSale = async (id: number): Promise<Sale> => {
  try {
    const response = await getProtected(
      `birddog/sales/${id}/`,
      undefined,
      "birddog"
    )
    const apiSale: ApiSaleResponse = response.data
    return transformApiSaleToFrontend(apiSale)
  } catch (error: any) {
    // Handle API errors from protected endpoints
    if (error?.response?.status === 404) {
      throw new SalesApiError("Sale not found", 404)
    }

    if (error?.response?.data) {
      const formattedError = formatApiError(error.response.data as ApiErrorType)
      throw new SalesApiError(
        formattedError,
        error.response.status as number,
        error.response.data as ApiErrorType
      )
    }

    // Re-throw our custom errors
    if (error instanceof SalesApiError) {
      throw error
    }

    throw new SalesApiError(
      `Failed to fetch sale: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    )
  }
}

// API Bid Types (based on API docs)
interface ApiBidResponse {
  id: number
  sale: number
  bidder_name: string
  bid_type: "lump sum" | "pay as cut"
  total_amount?: number
  product_prices?: Array<{
    product_name: string
    price_per_unit: number
  }>
  submitted_at: string | null
  created_at: string
  updated_at: string
}

// Transform API bid response to frontend format
const transformApiBidToFrontend = (apiBid: ApiBidResponse): Bid => {
  const baseBid = {
    id: apiBid.id,
    sale_id: apiBid.sale,
    bidder_name: apiBid.bidder_name,
    submitted_at: new Date(apiBid.submitted_at || apiBid.created_at),
  }

  if (apiBid.bid_type === "lump sum") {
    return {
      ...baseBid,
      type: "lump sum",
      total_amount: apiBid.total_amount || 0,
    } as LumpSumBid
  } else {
    return {
      ...baseBid,
      type: "pay as cut",
      product_prices: apiBid.product_prices || [],
    } as PayAsCutBid
  }
}

// Fetch bids for a specific sale
export const fetchSaleBids = async (saleId: number): Promise<Bid[]> => {
  try {
    const response = await getProtected(
      `birddog/sales/${saleId}/bids/`,
      undefined,
      "birddog"
    )
    const apiBids: ApiBidResponse[] = response.data
    return apiBids.map(transformApiBidToFrontend)
  } catch (error: any) {
    // Handle API errors from protected endpoints
    if (error?.response?.status === 404) {
      throw new SalesApiError("Sale not found", 404)
    }

    if (error?.response?.data) {
      const formattedError = formatApiError(error.response.data as ApiErrorType)
      throw new SalesApiError(
        formattedError,
        error.response.status as number,
        error.response.data as ApiErrorType
      )
    }

    // Re-throw our custom errors
    if (error instanceof SalesApiError) {
      throw error
    }

    throw new SalesApiError(
      `Failed to fetch bids: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    )
  }
}

// Fetch the current user's bid for a specific sale
export const fetchUserBidForSale = async (
  saleId: number
): Promise<Bid | null> => {
  try {
    const response = await getProtected(
      `birddog/sales/${saleId}/bids/`,
      undefined,
      "birddog"
    )
    const apiBids: ApiBidResponse[] = response.data

    // For now, we assume the API returns only the current user's bids
    // If the API returns all bids, we would need to filter by current user
    // Since we don't have user ID filtering in the current API, we'll take the first bid
    // This assumes the API is already filtering by the authenticated user
    if (apiBids.length > 0) {
      return transformApiBidToFrontend(apiBids[0])
    }

    return null
  } catch (error: any) {
    // Handle API errors from protected endpoints
    if (error?.response?.status === 404) {
      return null // No bids found, which is valid
    }

    if (error?.response?.data) {
      const formattedError = formatApiError(error.response.data as ApiErrorType)
      throw new SalesApiError(
        formattedError,
        error.response.status as number,
        error.response.data as ApiErrorType
      )
    }

    // Re-throw our custom errors
    if (error instanceof SalesApiError) {
      throw error
    }

    throw new SalesApiError(
      `Failed to fetch user bid: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    )
  }
}

// Validate sale completeness before publishing
const validateSaleForPublishing = (sale: Sale): string[] => {
  const errors: string[] = []

  if (!sale.name?.trim()) {
    errors.push("Sale name is required")
  }

  if (!sale.location_text?.trim()) {
    errors.push("Location is required")
  }

  if (
    !sale.boundary_geom ||
    (sale.boundary_geom.type === "Polygon" &&
      sale.boundary_geom.coordinates[0].length === 0)
  ) {
    errors.push("Sale boundary must be defined on the map")
  }

  if (!sale.timber_products || sale.timber_products.length === 0) {
    errors.push("At least one timber product is required")
  }

  if (
    !sale.bid_start_datetime ||
    !sale.bid_stop_datetime ||
    !sale.bid_select_datetime
  ) {
    errors.push("All bidding dates/times must be set")
  }

  // Validate date logic
  if (
    sale.bid_start_datetime &&
    sale.bid_stop_datetime &&
    sale.bid_start_datetime >= sale.bid_stop_datetime
  ) {
    errors.push("Bid start time must be before bid stop time")
  }

  if (
    sale.bid_stop_datetime &&
    sale.bid_select_datetime &&
    sale.bid_stop_datetime >= sale.bid_select_datetime
  ) {
    errors.push("Bid stop time must be before bid selection time")
  }

  return errors
}

// API function to publish a sale (change status from draft to active)
export const publishSale = async (saleId: number): Promise<ApiSaleResponse> => {
  try {
    // First fetch the current sale to validate it can be published
    const currentSale = await fetchSale(saleId)

    // Check if sale is in draft status
    if (currentSale.status !== "draft") {
      throw new SalesApiError(
        `Sale cannot be published. Current status: ${currentSale.status}`,
        400
      )
    }

    // Validate sale completeness
    const validationErrors = validateSaleForPublishing(currentSale)
    if (validationErrors.length > 0) {
      throw new SalesApiError(
        `Sale cannot be published due to the following issues:\n${validationErrors.join(
          "\n"
        )}`,
        400
      )
    }

    // Make API request to update status using protected endpoint
    const response = await patchProtected(
      `birddog/sales/${saleId}/`,
      {
        status: "active",
        published_at: new Date().toISOString(),
      },
      "birddog"
    )
    const publishedSale: ApiSaleResponse = response.data
    return publishedSale
  } catch (error: any) {
    // Re-throw our custom errors
    if (error instanceof SalesApiError) {
      throw error
    }

    // Handle API errors from protected endpoints
    if (error?.response?.data) {
      const formattedError = formatApiError(error.response.data as ApiErrorType)
      throw new SalesApiError(
        formattedError,
        error.response.status as number,
        error.response.data as ApiErrorType
      )
    }

    // Handle network errors or other unexpected errors
    if (error instanceof Error) {
      throw new SalesApiError(`Network error: ${error.message}`, 0)
    }

    throw new SalesApiError(
      "An unexpected error occurred while publishing the sale"
    )
  }
}

// Main API function to update a sale
export const updateSale = async (
  saleId: number,
  saleData: SaleCreateData
): Promise<ApiSaleResponse> => {
  // Client-side validation
  const validationErrors = validateSaleData(saleData)
  if (validationErrors.length > 0) {
    throw new SalesApiError(
      `Validation failed:\n${validationErrors.join("\n")}`,
      400
    )
  }

  try {
    // Transform data to API format
    const apiPayload = transformSaleDataToApiFormat(saleData)

    // Make API request using protected endpoint
    const response = await patchProtected(
      `birddog/sales/${saleId}/`,
      apiPayload,
      "birddog"
    )

    const updatedSale: ApiSaleResponse = response.data
    return updatedSale
  } catch (error: any) {
    // Re-throw our custom errors
    if (error instanceof SalesApiError) {
      throw error
    }

    // Handle API errors from protected endpoints
    if (error?.response?.data) {
      const formattedError = formatApiError(error.response.data as ApiErrorType)
      throw new SalesApiError(
        formattedError,
        error.response.status as number,
        error.response.data as ApiErrorType
      )
    }

    // Handle network errors or other unexpected errors
    if (error instanceof Error) {
      throw new SalesApiError(`Network error: ${error.message}`, 0)
    }

    throw new SalesApiError("An unexpected error occurred")
  }
}
